import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { calcularTotales, formatearCOP } from '@tanteo/compartido';
import type { Pedido } from '../../lib/pedidos';

/** Minutos que marcan el punto de un pedido: verde hasta 15, amarillo hasta 45, rojo después. */
function tonoDe(minutos: number): string {
  if (minutos < 15) return 'bg-success';
  if (minutos < 45) return 'bg-warning';
  return 'bg-destructive';
}

function nombreDe(p: Pedido): string {
  return p.nombre ? `Pedido ${p.numero}: ${p.nombre}` : `Pedido ${p.numero}`;
}

function Punto({ minutos }: { minutos: number }) {
  return <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${tonoDe(minutos)}`} aria-hidden />;
}

function claseChip(activa: boolean): string {
  return `rounded-control inline-flex min-h-(--alto-control) shrink-0 items-center gap-2 border px-4 py-1.5 text-contenido font-bold whitespace-nowrap ${
    activa ? 'border-primary bg-primary/15 text-primary' : 'border-border bg-card text-foreground hover:bg-surface'
  }`;
}

/**
 * La franja de arriba del mostrador: MOSTRADOR, un chip por pedido y «Nuevo pedido». Si los chips no caben en
 * el ancho, todos los pedidos pasan a una lista flotante, cuyo Esc queda atendido para no contar en Esc,Esc.
 */
export function FranjaPedidos({
  pedidos,
  activo,
  hoy,
  guardado,
  onMostrador,
  onAbrir,
  onNuevo,
  onCerrarLista,
}: {
  pedidos: Pedido[];
  /** El pedido en pantalla; null es MOSTRADOR. */
  activo: Pedido | null;
  hoy: string;
  /** «guardado hh:mm», o el aviso de que no se pudo guardar; solo con un pedido en pantalla. */
  guardado: string | null;
  onMostrador: () => void;
  onAbrir: (p: Pedido) => void;
  onNuevo: () => void;
  onCerrarLista: () => void;
}) {
  const [abierta, setAbierta] = useState(false);
  const [enLista, setEnLista] = useState(false);
  const [ahora, setAhora] = useState(() => Date.now());
  const franja = useRef<HTMLDivElement>(null);
  const medida = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLDivElement>(null);

  const minutos = (p: Pedido) => Math.max(0, Math.floor((ahora - p.abiertoEn) / 60_000));
  const totalDe = (p: Pedido) => formatearCOP(calcularTotales(p.venta.lineas, p.venta.descuento).total);
  const etiqueta = (p: Pedido) =>
    `${nombreDe(p)} · ${totalDe(p)} · ${p.fecha !== hoy ? 'de ayer' : `${minutos(p)} min`}`;

  // Los minutos de cada pedido avanzan solos.
  useEffect(() => {
    const reloj = setInterval(() => setAhora(Date.now()), 30_000);
    return () => clearInterval(reloj);
  }, []);

  // Chips o lista según el ancho real: una copia invisible con todos los chips dice si caben.
  useLayoutEffect(() => {
    const medir = () => {
      if (franja.current && medida.current) setEnLista(medida.current.scrollWidth > franja.current.clientWidth);
    };
    medir();
    const observador = new ResizeObserver(medir);
    if (franja.current) observador.observe(franja.current);
    return () => observador.disconnect();
  }, [pedidos, activo, ahora]);

  useEffect(() => {
    if (!abierta) return;
    const cerrar = () => {
      setAbierta(false);
      onCerrarLista();
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      cerrar();
    };
    const alTocar = (e: MouseEvent) => {
      if (!lista.current?.contains(e.target as Node)) cerrar();
    };
    window.addEventListener('keydown', alTeclear, true);
    window.addEventListener('mousedown', alTocar);
    return () => {
      window.removeEventListener('keydown', alTeclear, true);
      window.removeEventListener('mousedown', alTocar);
    };
  }, [abierta, onCerrarLista]);

  const chips = (): ReactNode =>
    pedidos.map((p) => (
      <button
        key={p.id}
        className={claseChip(activo?.id === p.id)}
        onClick={() => onAbrir(p)}
        data-e2e={`chip-pedido-${p.numero}`}
      >
        <Punto minutos={minutos(p)} />
        {etiqueta(p)}
      </button>
    ));
  const mostrador = (
    <button className={claseChip(activo === null)} onClick={onMostrador} data-e2e="chip-mostrador">
      MOSTRADOR
    </button>
  );
  const nuevo = (
    <button className={claseChip(false)} onClick={onNuevo} data-e2e="nuevo-pedido">
      Nuevo pedido
    </button>
  );
  const masViejo = pedidos[0];

  return (
    <div className="relative" data-e2e="franja-pedidos">
      <div ref={medida} className="pointer-events-none invisible absolute flex gap-2" aria-hidden>
        <span className={claseChip(false)}>MOSTRADOR</span>
        {pedidos.map((p) => (
          <span key={p.id} className={claseChip(false)}>
            <Punto minutos={minutos(p)} />
            {etiqueta(p)}
          </span>
        ))}
        <span className={claseChip(false)}>Nuevo pedido</span>
      </div>
      <div ref={franja} className="flex items-center gap-2">
        {mostrador}
        {!enLista && chips()}
        {enLista && masViejo && (
          <div className="relative" ref={lista}>
            <button
              className={claseChip(activo !== null)}
              onClick={() => setAbierta((a) => !a)}
              aria-haspopup="menu"
              aria-expanded={abierta}
              data-e2e="chip-pedidos"
            >
              {activo ? (
                nombreDe(activo)
              ) : (
                <>
                  <Punto minutos={minutos(masViejo)} />
                  Pedidos ({pedidos.length}) ({minutos(masViejo)} min)
                </>
              )}
              <span aria-hidden>▾</span>
            </button>
            {abierta && (
              <div
                role="menu"
                className="bg-card border-border rounded-card absolute left-0 z-40 mt-1 max-h-80 w-96 max-w-[90vw] overflow-y-auto border p-1 shadow-lg"
                data-e2e="lista-pedidos"
              >
                {pedidos.map((p) => (
                  <button
                    key={p.id}
                    role="menuitem"
                    onClick={() => {
                      setAbierta(false);
                      onAbrir(p);
                    }}
                    className={`rounded-control flex w-full items-center gap-2 px-3 py-2 text-left text-contenido hover:bg-surface ${
                      activo?.id === p.id ? 'bg-primary/15 font-bold' : ''
                    }`}
                    data-e2e={`pedido-${p.numero}`}
                  >
                    <Punto minutos={minutos(p)} />
                    <span className="min-w-0 flex-1 truncate font-semibold">{nombreDe(p)}</span>
                    <span className="num">{totalDe(p)}</span>
                    <span className="text-muted text-etiqueta">{p.fecha !== hoy ? 'de ayer' : `${minutos(p)} min`}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {nuevo}
        {activo && guardado && (
          <span className="text-muted text-etiqueta whitespace-nowrap" data-e2e="pedido-guardado">
            {guardado}
          </span>
        )}
      </div>
    </div>
  );
}
